/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2022 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { observable, action } from "mobx";
import { NOTIFICATION_TYPE, NOTIFICATION_CLOSE_REASON } from "constants/constants";

class NotificationViewStore {
  @observable notifications;
  @observable notificationBanners;
  @observable currentNotificationBannerHeight;
  @observable renderBannerBeforeDoc;

  constructor() {
    this.init();
    this.renderBannerBeforeDoc = false;
  }

  initStore(docApi, stores) {
    if (docApi) {
      this.docApi = docApi;
    }
    if (stores) {
      this.stores = stores;
    }
  }

  @action
  initToasts() {
    this.notifications = [];
  }

  @action
  initBanners() {
    this.notificationBanners = [];
    this.currentNotificationBannerHeight = 0;
  }

  @action
  init() {
    this.initToasts();
    this.initBanners();
  }

  @action
  addNotification(
    msg = "modernviewer.notificationview.defaultMsg",
    opts = {
      overrideClassName: null,
      shouldRemoveActiveToasts: false,
      shouldRemoveActiveBanners: false,
      onClose: () => {},
      timeout: 0,
      type: null,
      variant: null,
      actionLabel: null,
      onAction: () => {},
      closeOnAction: true,
      messageIcon: null
    }
  ) {
    if (opts.shouldRemoveActiveToasts) {
      this.initToasts();
    }
    if (opts.shouldRemoveActiveBanners) {
      this.initBanners();
    }

    let notificationArray = [];
    if (opts.type && opts.type === NOTIFICATION_TYPE.BANNER) {
      notificationArray = [...this.notificationBanners];
      opts.type = NOTIFICATION_TYPE.BANNER;
    } else {
      notificationArray = [...this.notifications];
      opts.type = NOTIFICATION_TYPE.TOAST;
    }

    const id = new Date().getTime();
    if (!opts.variant) {
      opts.variant = "info";
    }
    const newNotification = {
      id,
      msg,
      options: opts
    };
    notificationArray.push(newNotification);
    if (newNotification.options.timeout) {
      setTimeout(() => {
        opts.type === NOTIFICATION_TYPE.TOAST
          ? this.removeToastNotification(id, NOTIFICATION_CLOSE_REASON.TIMEOUT)
          : this.removeBannerNotification(id, NOTIFICATION_CLOSE_REASON.TIMEOUT);
      }, newNotification.options.timeout);
    }

    if (opts.type === NOTIFICATION_TYPE.TOAST) {
      this.notifications = notificationArray;
      /* Max number of allowed toasts is 3 */
      if (this.notifications.length > 3) {
        this.removeToastNotification(this.notifications[0].id, NOTIFICATION_CLOSE_REASON.ADD_NEW);
      }
    } else {
      this.notificationBanners = notificationArray;
      /* Max number of allowed banners is 1 */
      if (this.notificationBanners.length > 1) {
        this.removeBannerNotification(this.notificationBanners[0].id, NOTIFICATION_CLOSE_REASON.ADD_NEW);
      }
    }

    return id;
  }

  @action
  removeToastNotification(id, notificationCloseReason = NOTIFICATION_CLOSE_REASON.CLOSE_CLICK) {
    let isNotificationRemoved = false;
    const notifcationObjIdx = this.notifications.findIndex(ele => ele.id === id);
    if (notifcationObjIdx > -1) {
      this.notifications[notifcationObjIdx].options.onClose &&
        this.notifications[notifcationObjIdx].options.onClose(notificationCloseReason);
      this.notifications.splice(notifcationObjIdx, 1);
      this.notifications = [...this.notifications];
      isNotificationRemoved = true;
    }
    return isNotificationRemoved;
  }

  @action
  removeBannerNotification(id, bannerCloseReason = NOTIFICATION_CLOSE_REASON.CLOSE_CLICK) {
    let isNotificationRemoved = false;
    const notifcationObjIdx = this.notificationBanners.findIndex(ele => ele.id === id);
    if (notifcationObjIdx > -1) {
      this.notificationBanners[notifcationObjIdx].options.onClose &&
        this.notificationBanners[notifcationObjIdx].options.onClose(bannerCloseReason);
      this.notificationBanners.splice(notifcationObjIdx, 1);
      this.notificationBanners = [...this.notificationBanners];
      isNotificationRemoved = true;
    }
    return isNotificationRemoved;
  }

  @action
  removeAllNotifications() {
    this.notifications.forEach(notification => {
      const { options } = notification;
      options.onClose && options.onClose(NOTIFICATION_CLOSE_REASON.CLEAR_ALL);
    });
    this.notificationBanners.forEach(notification => {
      const { options } = notification;
      options.onClose && options.onClose(NOTIFICATION_CLOSE_REASON.CLEAR_ALL);
    });
    this.init();
  }

  @action
  removeNotification(id, removeNotificationReason) {
    let isNotificationRemoved = false;
    isNotificationRemoved = this.removeToastNotification(id, removeNotificationReason);
    if (!isNotificationRemoved) {
      isNotificationRemoved = this.removeBannerNotification(id, removeNotificationReason);
    }
    return isNotificationRemoved;
  }

  @action
  setNotificationHeight(id, height) {
    this.notifications = this.notifications.map(notification => {
      if (notification.id === id) {
        notification.height = height;
      }
      return notification;
    });
  }

  @action
  setNotificationBannerHeight(id, height) {
    this.currentNotificationBannerHeight = height;
    this.notificationBanners = this.notificationBanners.map(notificationBanner => {
      if (notificationBanner.id === id) {
        notificationBanner.height = height;
      }
      return notificationBanner;
    });
  }

  @action
  setShouldRenderBannerBeforeDoc(renderBannerBeforeDoc) {
    this.renderBannerBeforeDoc = renderBannerBeforeDoc;
  }
}

export { NotificationViewStore };
